import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { addWeeks, getISOWeek, getISOWeekYear, setISOWeek, setISOWeekYear, startOfISOWeek } from 'date-fns';
import { X, Copy, Loader2, CheckCircle2, AlertTriangle, ArrowRight, Users, CheckSquare, Square, Info } from 'lucide-react';
import { Button } from '../../../components/Shared/Button';
import { usePlanningWeeksStore, type CopyWeekResult } from '../../../store/usePlanningWeeksStore';
import { showApiError } from '../../../errors';
import { formatDate } from '../../../utils/dateUtils';
import type { EmployeePlanning } from '../../../types/planning';

interface CopyWeekDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sourceYear: number;
  sourceWeek: number;
  /** Direkt nach erfolgreichem Übernehmen: Anzahl kopierter Aufgaben und Zielwoche (Toast). */
  onCopied: (copiedCount: number, targetWeek: number, targetYear: number) => void;
  /**
   * Beim Schließen nach einem Übernehmen: Plan neu laden. why: erst hier — das Nachladen
   * blendet die Seite kurz aus und würde den Dialog mitsamt Ergebnis neu aufbauen.
   */
  onCopyComplete: () => void;
  employees?: EmployeePlanning[];
  selectedEmployeeIds?: string[];
}

/** ISO-Woche nach der gegebenen — inkl. KW 53 und Jahreswechsel. */
function nextIsoWeek(year: number, week: number): { year: number; week: number } {
  const monday = startOfISOWeek(setISOWeek(setISOWeekYear(new Date(year, 5, 15), year), week));
  const next = addWeeks(monday, 1);
  return { year: getISOWeekYear(next), week: getISOWeek(next) };
}

export function CopyWeekDialog({
  isOpen,
  onClose,
  sourceYear,
  sourceWeek,
  onCopied,
  onCopyComplete,
  employees = [],
  selectedEmployeeIds = [],
}: CopyWeekDialogProps) {
  const { t } = useTranslation('planning');
  const copyWeek = usePlanningWeeksStore((s) => s.copyWeek);
  const [targetYear, setTargetYear] = useState(sourceYear);
  const [targetWeek, setTargetWeek] = useState(sourceWeek + 1);
  const [isCopying, setIsCopying] = useState(false);
  const [result, setResult] = useState<CopyWeekResult | null>(null);
  const [copyEmployeeIds, setCopyEmployeeIds] = useState<string[]>([]);

  // why: only days with tasks count — the backfill creates empty work days for everyone.
  const employeesWithEntries = employees.filter((emp) => emp.days.some((d) => d.tasks.length > 0));

  // Reset state when the dialog opens — only then: the grid reloads after copying, which hands in
  // new employees and would otherwise wipe the result view.
  const wasOpen = useRef(false);
  useEffect(() => {
    const opened = isOpen && !wasOpen.current;
    wasOpen.current = isOpen;
    if (opened) {
      const next = nextIsoWeek(sourceYear, sourceWeek);
      setTargetYear(next.year);
      setTargetWeek(next.week);
      setIsCopying(false);
      setResult(null);
      const withEntries = employees
        .filter((emp) => emp.days.some((d) => d.tasks.length > 0))
        .map((emp) => emp.id);
      const preSelected = selectedEmployeeIds.filter((id) => withEntries.includes(id));
      setCopyEmployeeIds(preSelected.length > 0 ? preSelected : withEntries);
    }
  }, [isOpen, sourceYear, sourceWeek, employees, selectedEmployeeIds]);

  const toggleEmployee = (id: string) => {
    setCopyEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((eid) => eid !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (copyEmployeeIds.length === employeesWithEntries.length) {
      setCopyEmployeeIds([]);
    } else {
      setCopyEmployeeIds(employeesWithEntries.map((e) => e.id));
    }
  };

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (copyEmployeeIds.length === 0) return;

    setIsCopying(true);
    try {
      const response = await copyWeek(
        { year: sourceYear, week: sourceWeek },
        { year: targetYear, week: targetWeek },
        copyEmployeeIds,
      );
      setResult(response);
      onCopied(response.copiedCount ?? 0, targetWeek, targetYear);
    } catch (error) {
      // why: the old dialog swallowed errors and showed "nothing to copy" instead.
      showApiError(error);
    } finally {
      setIsCopying(false);
    }
  };

  const handleClose = () => {
    const copied = (result?.copiedCount ?? 0) > 0;
    setResult(null);
    setIsCopying(false);
    onClose();
    if (copied) onCopyComplete();
  };

  const skipped = result?.skipped ?? [];
  const allSelected = copyEmployeeIds.length === employeesWithEntries.length && employeesWithEntries.length > 0;
  const reasonLabel = (code?: string | null) =>
    code ? t(`copyWeek.reasons.${code.split('.').pop()}`, { defaultValue: code }) : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Copy className="w-5 h-5" />
              {t('copyWeek.title')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('copyWeek.subtitle')}</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label={t('copyWeek.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {!result ? (
            <>
              {/* Source / Target Selection */}
              <div className="flex items-center gap-4">
                <div className="flex-1 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
                    {t('copyWeek.source')}
                  </label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                    {t('copyWeek.weekLabel', { week: sourceWeek, year: sourceYear })}
                  </p>
                </div>

                <ArrowRight className="w-6 h-6 text-gray-400 flex-shrink-0" />

                <div className="flex-1 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <label className="block text-xs font-medium text-blue-600 dark:text-blue-400 mb-2 uppercase tracking-wide">
                    {t('copyWeek.target')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('copyWeek.week')}</label>
                      <input
                        type="number"
                        min={1}
                        max={53}
                        value={targetWeek}
                        onChange={(e) => setTargetWeek(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm tabular-nums"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('copyWeek.year')}</label>
                      <input
                        type="number"
                        min={2020}
                        max={2100}
                        value={targetYear}
                        onChange={(e) => setTargetYear(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm tabular-nums"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Employee Selection */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('copyWeek.selectEmployees', { selected: copyEmployeeIds.length, total: employeesWithEntries.length })}
                    </span>
                  </div>
                  {employeesWithEntries.length > 1 && (
                    <button onClick={toggleAll} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                      {allSelected ? t('copyWeek.selectNone') : t('copyWeek.selectAll')}
                    </button>
                  )}
                </div>

                {employeesWithEntries.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('copyWeek.noEmployees')}</p>
                ) : (
                  <div className="space-y-1.5">
                    {employeesWithEntries.map((emp) => {
                      const isChecked = copyEmployeeIds.includes(emp.id);
                      const taskCount = emp.days.reduce((sum, d) => sum + d.tasks.length, 0);
                      const workDays = emp.days.filter((d) => d.tasks.length > 0).length;

                      return (
                        <button
                          key={emp.id}
                          onClick={() => toggleEmployee(emp.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                            isChecked
                              ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                              : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
                          }`}
                        >
                          {isChecked ? (
                            <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium ${
                              isChecked ? 'text-blue-800 dark:text-blue-200' : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              {emp.name}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 tabular-nums">
                            {t('copyWeek.employeeSummary', { count: taskCount, days: workDays })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex gap-2">
                <Info className="w-4 h-4 text-yellow-700 dark:text-yellow-300 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800 dark:text-yellow-200">{t('copyWeek.hint')}</p>
              </div>
            </>
          ) : (
            <>
              {/* Results */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-green-800 dark:text-green-200 tabular-nums">
                    {t('copyWeek.copied', { count: result.copiedCount ?? 0 })}
                  </span>
                </div>
                {(result.duplicateCount ?? 0) > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <Info className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 tabular-nums">
                      {t('copyWeek.duplicates', { count: result.duplicateCount ?? 0 })}
                    </span>
                  </div>
                )}
                {skipped.length > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-200 tabular-nums">
                      {t('copyWeek.skipped', { count: skipped.length })}
                    </span>
                  </div>
                )}
              </div>

              {skipped.length > 0 ? (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
                  {skipped.map((item, index) => (
                    <div key={index} className="p-3 flex items-center gap-3 bg-amber-50/50 dark:bg-amber-900/10">
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 dark:text-white tabular-nums">
                          {formatDate(item.targetDate)} · {item.employeeName} · {item.taskName}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{reasonLabel(item.reasonCode)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (result.copiedCount ?? 0) === 0 && (result.duplicateCount ?? 0) === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">{t('copyWeek.nothing')}</div>
              ) : null}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          {!result ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isCopying}>
                {t('copyWeek.cancel')}
              </Button>
              <Button
                onClick={handleCopy}
                disabled={isCopying || targetWeek < 1 || targetWeek > 53 || copyEmployeeIds.length === 0}
              >
                {isCopying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t('copyWeek.copying')}
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    {t('copyWeek.submit', { count: copyEmployeeIds.length })}
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>{t('copyWeek.close')}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
